import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Eye, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ScoreBadge({ score, size = 'sm' }) {
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#EAB308' : '#EF4444';
  const bg = score >= 80 ? 'rgba(34,197,94,0.1)' : score >= 60 ? 'rgba(234,179,8,0.1)' : 'rgba(239,68,68,0.1)';
  const borderColor = score >= 80 ? 'rgba(34,197,94,0.3)' : score >= 60 ? 'rgba(234,179,8,0.3)' : 'rgba(239,68,68,0.3)';

  if (size === 'lg') {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: bg, border: `1px solid ${borderColor}` }}>
        <span className="text-sm font-bold" style={{ color }}>{score}%</span>
        <span className="text-[9px] text-white/40">QC</span>
      </div>
    );
  }
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color, background: bg }}>{score}%</span>
  );
}

function ScoreRow({ label, score }) {
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#EAB308' : '#EF4444';
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[10px] text-white/50">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-20 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: color }} />
        </div>
        <span className="text-[10px] font-mono font-bold w-8 text-right" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

export default function VLMQCPanel({ videoUrl, prompt, referenceImages = [], recipeType = 'seedance', slotLabels = [], token }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');

  const runQC = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/recipes/vlm-qc/analyze`, {
        video_url: videoUrl,
        prompt,
        reference_images: referenceImages,
        recipe_type: recipeType,
        slot_labels: slotLabels,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 150000 });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'QC analysis failed');
    } finally {
      setLoading(false);
    }
  };

  if (!videoUrl) return null;

  return (
    <div className="w-full" data-testid="vlm-qc-panel">
      {/* Run button or results */}
      {!result && !loading && (
        <button
          onClick={runQC}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all hover:scale-[1.01]"
          style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', color: '#A78BFA' }}
          data-testid="run-vlm-btn"
        >
          <Eye className="w-3.5 h-3.5" /> Run VLM Quality Check
        </button>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.1)' }}>
          <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
          <span className="text-[11px] text-violet-300 font-mono">Analyzing video with Gemini...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
          <span className="text-[10px] text-red-400">{error}</span>
          <button onClick={runQC} className="text-[9px] text-red-400/60 hover:text-red-400 underline">Retry</button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="vlm-qc-result">
          {/* Score header — always visible */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.02] transition-colors"
          >
            <div className="flex items-center gap-2">
              <ScoreBadge score={result.overall} size="lg" />
              <span className="text-[10px] text-white/40">Quality Score</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={(e) => { e.stopPropagation(); runQC(); }} className="text-[9px] text-white/20 hover:text-white/40 px-2 py-1 rounded hover:bg-white/5">Re-run</button>
              {expanded ? <ChevronUp className="w-3.5 h-3.5 text-white/30" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" />}
            </div>
          </button>

          {/* Expanded detail */}
          {expanded && (
            <div className="px-3 pb-3 space-y-1 border-t border-white/[0.04] pt-2">
              <ScoreRow label="Prompt Accuracy" score={result.prompt_accuracy} />
              <ScoreRow label="Character Consistency" score={result.character_consistency} />
              {recipeType === 'montage' && <ScoreRow label="Costume Match" score={result.costume_match} />}
              <ScoreRow label="Environment Match" score={result.environment_match} />
              
              {result.reasoning && (
                <div className="mt-2 pt-2 border-t border-white/[0.04]">
                  <p className="text-[9px] text-white/35 leading-relaxed">{result.reasoning}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { ScoreBadge };
